<template>
  <div class="article-view-mobile" :style="backgroundStyle">
    <!-- 背景图层 -->
    <div class="background-layer" :style="backgroundLayerStyle"></div>

    <!-- 文章标题横幅区域 -->
    <el-card class="article-header" :style="contentCardStyle">
      <div class="header-content mobile-only">
        <div class="header-row">
          <!-- 标题 -->
          <h2 class="article-title">{{ article.article_name }}</h2>

        </div>
        <!-- 时间 -->
        <div class="time-item">
          <i class="el-icon-date"></i>
          <span>{{ article.create_time }}</span>
        </div>
        <div v-if="article.update_time" class="time-item">
          <i class="el-icon-refresh"></i>
          <span>{{ article.update_time }}</span>
        </div>
        <!-- 类型、标签、阅读量 -->
        <div class="meta-row">
          <div class="left-items">
            <el-tag
              :color="getArticleTypeStyle(article.article_type)"
              effect="dark"
              class="article-type"
            >
              {{ article.article_type }}
            </el-tag>
            <el-tag
              v-for="tag in articleTags"
              :key="tag"
              size="small"
              class="article-tag"
            >
              {{ tag }}
            </el-tag>
          </div>
          
          <!-- 阅读量 -->
          <span class="read-count" style="padding-right: 0;">
            <i class="el-icon-view"></i>
            {{ readCount }}
          </span>
        </div>
      </div>
    </el-card>

    <!-- 正文区域 -->
    <div class="content-container">
      <div class="main-content-wrapper">
        <el-card class="article-content" :style="contentCardStyle">
          <div class="custom-markdown-container">
            <div
              ref="markdownDisplay"
              class="markdown-display"
              @click="handleContentClick"
            >
              <div class="markdown-content" v-html="renderedContent"></div>
              <div class="copyright-divider"></div>
              <div class="copyright-info" ref="copyrightInfo">
                <div class="mobile-copyright">
                  <div>Copyright © 2024 OIer之路 All rights reserved.</div>
                  <div>闽ICP备2025095302号-1</div>
                </div>
              </div>
            </div>
          </div>
        </el-card>
      </div>
    </div>

    <!-- 目录弹窗 -->
    <div v-if="showNavigation" class="mobile-navigation-overlay" @click="toggleNavigation">
      <div class="mobile-navigation-card" :style="contentCardStyle" @click.stop>
        <div class="mobile-navigation-header">
          <div class="title-wrapper">
            <span>文章目录</span>
          </div>
          <i class="el-icon-close" @click="toggleNavigation"></i>
        </div>
        <div class="mobile-navigation-content">
          <div
            v-for="(item, index) in navigationItems"
            :key="index"
            class="mobile-navigation-item"
            :class="{ active: currentHeadingId === item.id }"
            :style="{ paddingLeft: (item.level - 1) * 15 + 15 + 'px' }"
            @click="scrollToHeadingMobile(item.id)"
          >
            {{ item.text }}
          </div>
          <div v-if="navigationItems.length === 0" class="mobile-navigation-empty">
            无目录内容
          </div>
        </div>
      </div>
    </div>

    <!-- 按钮组 -->
    <div class="mobile-action-buttons">
      <div class="mobile-button-row">
        <el-button class="mobile-icon-btn" @click="goBack">
          <i class="el-icon-back"></i>
        </el-button>
      </div>
      <div class="mobile-button-row">
        <el-button class="mobile-icon-btn" @click="toggleNavigation">
          <i class="el-icon-tickets"></i>
        </el-button>
      </div>
      <div class="mobile-button-row">
        <div class="mobile-bg-settings-wrapper">
          <background-settings @background-updated="handleBgSettingsUpdated" />
        </div>
      </div>
      <!-- 全局菜单按钮、回到顶部按钮由 App.vue 提供 -->
    </div>
  </div>
</template>

<script>
import { getBlogArticle, increaseArticleReadCount } from '@/api/article'
import * as marked from 'marked'
import BackgroundSettings from '@/components/BackgroundSettings.vue'

export default {
  name: 'BlogArticleMobile',
  components: {
    BackgroundSettings
  },
  data() {
    return {
      article: {},
      renderedContent: '',
      navigationItems: [],
      currentHeadingId: '',
      showNavigation: false,
      readCount: 0,
      contentCardStyle: { backgroundColor: 'rgba(255, 255, 255, 0.8)' },
      backgroundLayerStyle: {},
      backgroundStyle: { backgroundColor: '#f0f2f5' },
      globalStyleEl: null
    }
  },
  computed: {
    articleTags() {
      return this.article.article_tag ? this.article.article_tag.split(',') : []
    }
  },
  created() {
    if (this.$route.params.articleCode) {
      this.getDetail(this.$route.params.articleCode)
    }
  },
  mounted() {
    this.addGlobalStyles()
    this.loadMathJax()
    
    // 添加背景设置调试日志
    this.$nextTick(() => {
      this.debugBgSettings()
    })
  },
  beforeDestroy() {
    this.removeGlobalStyles()
    this.cleanupMathJax()
  },
  methods: {
    async getDetail(articleCode) {
      try {
        const response = await getBlogArticle(articleCode)
        if (response.code === 200) {
          this.article = response.data
          
          // 检查文章是否已推送，未推送的文章不可访问
          if (!this.article.is_push) {
            this.$message.error('该文章暂未发布，无法访问')
            this.$router.push('/blog')
            return
          }
          
          this.readCount = response.data.read_count || 0
          this.renderMarkdown(this.article.article_info)

          // 绑定自定义容器点击事件
          this.$nextTick(() => {
            const container = this.$refs.markdownDisplay
            if (container) {
              container.querySelectorAll('.container-header').forEach(header => {
                header.onclick = () => {
                  const box = header.closest('.custom-container')
                  if (!box) return
                  box.classList.toggle('collapsed')
                }
              })
            }
          })

          // 增加阅读量
          this.increaseReadCount(articleCode)
        } else {
          this.$message.error(response.message || '获取文章详情失败')
        }
      } catch (error) {
        this.$message.error(error.message || '获取文章详情失败')
      }
    },
    
    // 增加阅读量
    async increaseReadCount(articleCode) {
      try {
        await increaseArticleReadCount(articleCode)
      } catch (error) {
        console.error('增加阅读量失败：', error)
      }
    },
    
    goBack() {
      this.$router.push('/blog')
    },

    // 预处理自定义容器
    preprocessCustomContainers(content) {
      const icon = { info:'ℹ️', tip:'💡', warning:'⚠️', danger:'❌', details:'▶️' }
      const color = { info:'#409EFF', tip:'#67C23A', warning:'#E6A23C', danger:'#F56C6C', details:'#909399' }

      // Step 1: 把所有 fenced code block 预渲染为 HTML，然后用占位符替换
      // 原因：最终 container div 内的代码块必须已经是 HTML，否则 marked.parse() 会把它当作原始文本
      const codeBlocks = []
      let cbIdx = 0
      content = content.replace(/```[\s\S]*?```/g, (match) => {
        // 预渲染为 HTML，这样放在 container div 内时 marked.parse() 不会二次处理
        const html = `<pre><code class="language-${(match.match(/```(\w+)/) || ['',''])[1] || 'plaintext'}">${match.replace(/```\w*\n?/g, '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>`
        const placeholder = `\u200BCB${cbIdx}\u200B`
        codeBlocks.push(html)
        cbIdx++
        return placeholder
      })

      let iterations = 0
      const maxIterations = 100

      while (iterations < maxIterations) {
        iterations++

        const opens = [], closes = []
        let m
        const allRe = /:::/g
        while ((m = allRe.exec(content)) !== null) {
          const pos = m.index
          const after = content[pos + 3]
          if (after !== undefined && /\w/.test(after)) {
            const typeMatch = content.substring(pos).match(/^:::(\w+)/)
            if (!typeMatch) continue
            const type = typeMatch[1]
            const titleMatch = content.substring(pos).match(/\[([^\]]*)\]/)
            const title = titleMatch ? titleMatch[1] : ''
            const lineEndMatch = content.substring(pos).search(/\n/)
            const lineEnd = lineEndMatch >= 0 ? pos + lineEndMatch : content.length
            opens.push({ start: pos, type, title, bodyStart: lineEnd + 1 })
          } else {
            const c1 = content[pos + 3]
            closes.push({ start: pos, lineEnd: c1 === '\n' ? pos + 3 : c1 === '\r' ? pos + 4 : content.length })
          }
        }

        let pair = null
        for (let oi = opens.length - 1; oi >= 0; oi--) {
          const open = opens[oi]
          let close = null
          for (const c of closes) { if (c.start > open.start) { close = c; break } }
          if (!close) continue
          const body = content.substring(open.bodyStart, close.start)
          if (!/^:::\w+/gm.test(body)) { pair = { open, close, body }; break }
        }
        if (!pair) break

        // 关键修复：用 marked 先把 body 预渲染为 HTML
        // 这样 bold/table/列表等都能正确渲染，而不是被 marked 当作原始文本
        // 内部已有的 container div（innermost 已处理）在这里会被 marked 当作 opaque，不受影响
        //
        // 修复：在 marked 解析前，先保护 body 中的 LaTeX 表达式，避免 $...$ 和 {} 等字符干扰列表解析
        // 解决方案：用字符遍历+括号深度计数的方式匹配公式，并排除转义序列 \$
        const bodyMathPlaceholders = []
        const protectMath = (text) => {
          let result = ''
          let i = 0
          while (i < text.length) {
            // 处理 $$...$$ 行间公式
            if (text[i] === '$' && text[i+1] === '$') {
              let j = i + 2
              let depth = 0
              while (j < text.length && !(text[j] === '$' && text[j+1] === '$' && depth === 0)) {
                if (text[j] === '{') depth++
                else if (text[j] === '}') depth--
                j++
              }
              const formula = text.substring(i, j + 2)
              bodyMathPlaceholders.push(formula)
              result += `MATH_PH_${bodyMathPlaceholders.length - 1}_END`
              i = j + 2
            }
            // 处理 $...$ 内联公式（排除转义序列 \$）
            else if (text[i] === '$' && text[i+1] !== '$') {
              let j = i + 1
              let depth = 0
              let inBrace = false
              while (j < text.length) {
                if (text[j] === '{' && !inBrace) {
                  inBrace = true
                  depth++
                } else if (text[j] === '}' && inBrace) {
                  depth--
                  if (depth === 0) inBrace = false
                } else if (text[j] === '$' && text[j-1] !== '\\' && depth === 0 && !inBrace) {
                  // 排除转义序列 \$，只有未被反斜杠转义的 $ 才是公式边界
                  break
                }
                j++
              }
              const formula = text.substring(i, j + 1)
              bodyMathPlaceholders.push(formula)
              result += `MATH_PH_${bodyMathPlaceholders.length - 1}_END`
              i = j + 1
            }
            // 普通字符
            else {
              result += text[i]
              i++
            }
          }
          return result
        }
        const protectedBody = protectMath(pair.body)
        const bodyHtml = marked.parse(protectedBody.trim()).replace(/MATH_PH_(\d+)_END/g, (_, idx) => bodyMathPlaceholders[parseInt(idx)])

        const t = pair.open.type.toLowerCase()
        const defaultCollapsed = true
        const collapsed = defaultCollapsed ? ' collapsed' : ''
        const ico = icon[t] || 'ℹ️'
        const col = color[t] || '#409EFF'
        const titleHtml = pair.open.title ? `<span class="container-title">${marked.parse(pair.open.title).replace(/^<p>(.*)<\/p>$/, '$1')}</span>` : ''
        content = content.substring(0, pair.open.start) +
          `<div class="custom-container${collapsed}" data-type="${t}" style="border-color:${col};background:rgba(${this._hexToRgb(col)},0.08)">` +
          `<div class="container-header"><span class="container-icon">${ico}</span>${titleHtml}<span class="container-toggle">▶</span></div>` +
          `<div class="container-content">${bodyHtml}</div></div>` +
          content.substring(pair.close.lineEnd)
      }

      // Step 2: 还原代码块（已预渲染为 HTML），直接替换占位符
      // 不再需要 ZWSP 转义（代码块已经是 HTML，不会被二次处理）
      for (let i = 0; i < codeBlocks.length; i++) {
        content = content.split(`\u200BCB${i}\u200B`).join(codeBlocks[i])
      }

      return content
    },
    _hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result ? `${parseInt(result[1],16)},${parseInt(result[2],16)},${parseInt(result[3],16)}` : '64,158,255'
    },

    preprocessLatexColors(content) {
      // 处理 LaTeX 颜色命令，完全复刻 BlogArticle.vue
      // 处理LaTeX公式中的<>符号，在<>两边添加空格，避免被解析为HTML标签
      content = content.replace(/(\$.*?)([^\\])<([^\\])(.*?\$)/g, '$1$2 < $3$4');
      content = content.replace(/(\$.*?)([^\\])>([^\\])(.*?\$)/g, '$1$2 > $3$4');
      content = content.replace(/(\$\$.*?)([^\\])<([^\\])(.*?\$\$)/g, '$1$2 < $3$4');
      content = content.replace(/(\$\$.*?)([^\\])>([^\\])(.*?\$\$)/g, '$1$2 > $3$4');
      // 处理没有空格的情况，如$i>j$
      content = content.replace(/(\$[^\$]*?)<([^\$\s]*?\$)/g, '$1 < $2');
      content = content.replace(/(\$[^\$]*?)>([^\$\s]*?\$)/g, '$1 > $2');
      content = content.replace(/(\$\$[^\$]*?)<([^\$\s]*?\$\$)/g, '$1 < $2');
      content = content.replace(/(\$\$[^\$]*?)>([^\$\s]*?\$\$)/g, '$1 > $2');
      // 处理单美元符号的情况下的颜色命令
      content = content.replace(/\$\\red\{([^}]*)\}\$/g, '$\\color{red}{$1}$');
      content = content.replace(/\$\\blue\{([^}]*)\}\$/g, '$\\color{blue}{$1}$');
      content = content.replace(/\$\\green\{([^}]*)\}\$/g, '$\\color{green}{$1}$');
      content = content.replace(/\$\\purple\{([^}]*)\}\$/g, '$\\color{purple}{$1}$');
      content = content.replace(/\$\\orange\{([^}]*)\}\$/g, '$\\color{orange}{$1}$');
      // 处理不带花括号的命令: $\redXXX$ -> $\color{red}{XXX}$（只处理单美元符号）
      content = content.replace(/\$\\red(\S+)\$/g, '$\\color{red}{$1}$');
      content = content.replace(/\$\\blue(\S+)\$/g, '$\\color{blue}{$1}$');
      content = content.replace(/\$\\green(\S+)\$/g, '$\\color{green}{$1}$');
      content = content.replace(/\$\\purple(\S+)\$/g, '$\\color{purple}{$1}$');
      content = content.replace(/\$\\orange(\S+)\$/g, '$\\color{orange}{$1}$');
      return content;
    },
    renderMarkdown(content) {
      try {
        console.log('开始渲染Markdown内容');

        // 预处理Markdown中的LaTeX颜色命令（必须在容器处理之前，否则会破坏容器HTML结构）
        content = this.preprocessLatexColors(content);

        // 预处理自定义容器
        content = this.preprocessCustomContainers(content);

        // 处理带颜色的行间公式 - 特殊处理
        content = content.replace(/\$\$\\red\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{red}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\blue\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{blue}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\green\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{green}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\purple\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{purple}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\orange\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{orange}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\color\{(\w+)\}\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{$1}{$2}$$\n</div>\n');

        // 临时保护LaTeX公式
        const mathExpressions = [];
        let protectedContent = content.replace(/(\$\$[\s\S]*?\$\$|\$[^\$\n]+?\$)/g, function(match) {
          console.log("保护LaTeX公式:", match.substring(0, 20) + (match.length > 20 ? "..." : ""));
          mathExpressions.push(match);
          return `MATH_PLACEHOLDER_${mathExpressions.length - 1}`;
        });
        
        // 配置marked
        const renderer = new marked.Renderer();
        
        // 自定义任务列表渲染
        renderer.list = function(body, ordered, start) {
          const hasTaskItems = body.includes('input[type="checkbox"]');
          const listType = ordered ? 'ol' : 'ul';
          const startAttr = ordered && start !== 1 ? ` start="${start}"` : '';
          if (hasTaskItems) {
            return `<${listType} class="task-list"${startAttr}>\n${body}</${listType}>\n`;
          } else {
            return `<${listType}${startAttr}>\n${body}</${listType}>\n`;
          }
        };
        
        renderer.listitem = function(text) {
          if (text.includes('input[type="checkbox"]')) {
            return `<li class="task-list-item">${text}</li>\n`;
          } else {
            return `<li>${text}</li>\n`;
          }
        };
        
        // 自定义代码渲染 - 简单返回代码，后续由highlight.js处理
        renderer.code = function(code, language) {
          // 确保HTML标签被转义
          code = code.replace(/</g, '&lt;').replace(/>/g, '&gt;');
          return `<pre><code class="language-${language || 'plaintext'}">${code}</code></pre>`;
        };
        
        // 自定义引用渲染
        renderer.blockquote = function(quote) {
          // 移除<p>标签前后的空格，以避免样式问题
          return `<blockquote class="custom-blockquote">${quote.trim()}</blockquote>`;
        };
        
        marked.setOptions({
          renderer: renderer,
          breaks: true,
          gfm: true,
          headerIds: true,
          langPrefix: 'language-'
        });
        
        // 渲染markdown内容
        let renderedHtml = marked.parse(protectedContent);
        
        // 还原LaTeX公式
        renderedHtml = renderedHtml.replace(/MATH_PLACEHOLDER_(\d+)/g, function(match, index) {
          const formula = mathExpressions[parseInt(index)];
          console.log("还原LaTeX公式:", formula.substring(0, 20) + (formula.length > 20 ? "..." : ""));
          // 确保公式不被HTML转义
          return formula;
        });
        
        this.renderedContent = renderedHtml;
        
        // 在下一个渲染周期处理LaTeX公式和代码高亮
        this.$nextTick(async () => {
          // 处理LaTeX公式
          if (window.MathJax && window.MathJax.Hub) {
            console.log('使用已加载的MathJax处理公式');
            window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub, this.$refs.markdownDisplay]);
          } else {
            // 如果MathJax还没有加载完成，等待加载完成后再渲染
            console.log('等待MathJax加载后处理公式');
            
            // 创建一次性事件处理函数
            document.addEventListener('mathjax-rendered', () => {
              if (window.MathJax && window.MathJax.Hub) {
                window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub, this.$refs.markdownDisplay]);
              }
            }, { once: true });
          }
          
          // 先扫描标题生成导航
          this.scanHeadingsAndGenerateNavigation();
          
          // 手动处理引用样式
          this.enhanceBlockquotes();
          
          // 加载highlight.js并处理代码高亮
          try {
            await this.highlightCode();
            // 不在这里初始化代码块，而是等待 MathJax 渲染完成事件触发时初始化
            // 这样可以避免代码块被初始化多次
          } catch (err) {
            console.error('代码高亮处理失败:', err);
          }
          
          // 增强数学公式颜色
          this.enhanceMathColors();
          
          // 增强任务列表样式
          this.enhanceTaskLists();
        
        // 内容渲染完成后再次检查背景设置
        setTimeout(() => {
          console.log('=== 内容渲染完成后的背景设置调试信息 ===')
          this.debugBgSettings()
        }, 1000)
        });
      } catch (error) {
        console.error('Markdown渲染失败:', error);
        // 降级处理 - 直接显示原始内容
        const plainTextContent = `<pre>${content.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`;
        this.renderedContent = plainTextContent;
      }
    },
    
    // 增强引用样式
    enhanceBlockquotes() {
      if (!this.$refs.markdownDisplay) return;
      
      const blockquotes = this.$refs.markdownDisplay.querySelectorAll('blockquote');
      blockquotes.forEach(blockquote => {
        // 确保添加自定义类
        blockquote.classList.add('custom-blockquote');
        
        // 添加引号图标
        if (!blockquote.querySelector('.quote-icon')) {
          const quoteIcon = document.createElement('span');
          quoteIcon.className = 'quote-icon';
          quoteIcon.innerHTML = '❝';
          blockquote.insertBefore(quoteIcon, blockquote.firstChild);
        }
      });
    },
    
    // 扫描标题并生成导航
    scanHeadingsAndGenerateNavigation() {
      if (!this.$refs.markdownDisplay) return
      
      // 清空导航项
      this.navigationItems = []
      
      // 查找内容区域中的所有标题元素
      const headings = this.$refs.markdownDisplay.querySelectorAll('h1, h2, h3, h4, h5, h6')
      
      // 处理每个标题
      headings.forEach((heading, index) => {
        // 获取标题级别和文本
        const level = parseInt(heading.tagName.substring(1))
        const text = heading.textContent.trim()
        
        // 为标题添加唯一ID
        const id = heading.id || `heading-${index}`
        heading.id = id
        
        // 添加到导航项
        this.navigationItems.push({
          id: id,
          level: level,
          text: text
        })
      })
    },
    // 代码高亮/行号/复制，完全复用 BlogArticle.vue
    async highlightCode() {
      // 加载 highlight.js
      if (!window.hljs) {
        const cssLink = document.createElement('link')
        cssLink.rel = 'stylesheet'
        cssLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.7.0/styles/github-dark.min.css'
        document.head.appendChild(cssLink)
        const script = document.createElement('script')
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.7.0/highlight.min.js'
        script.async = true
        document.head.appendChild(script)
        await new Promise(resolve => { script.onload = resolve })
      }
      // 代码块高亮
      const blocks = this.$refs.markdownDisplay?.querySelectorAll('pre code') || []
      blocks.forEach(block => {
        window.hljs.highlightElement(block)
      })
      // 行号和复制按钮
      const pres = this.$refs.markdownDisplay?.querySelectorAll('pre') || []
      pres.forEach(pre => {
        // 行号
        if (!pre.querySelector('.line-numbers')) {
          const code = pre.querySelector('code')
          if (code) {
            const lines = code.innerHTML.split('\n')
            const lineNumbersEl = document.createElement('div')
            lineNumbersEl.className = 'line-numbers'
            lineNumbersEl.textContent = Array.from({ length: lines.length }, (_, i) => String(i + 1).padStart(2)).join('\n')
            pre.appendChild(lineNumbersEl)
          }
        }
        // 复制按钮
        if (!pre.querySelector('.copy-code-btn')) {
          const code = pre.querySelector('code')
          const copyBtn = document.createElement('button')
          copyBtn.className = 'copy-code-btn'
          copyBtn.textContent = '复制'
          copyBtn.onclick = async e => {
            e.stopPropagation()
            const textToCopy = code.textContent
            
            // 尝试多种复制方法
            try {
              // 方法1: 使用 navigator.clipboard API (现代浏览器)
              if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(textToCopy)
                copyBtn.textContent = '已复制'
                setTimeout(() => { copyBtn.textContent = '复制' }, 2000)
                return
              }
              
              // 方法2: 使用 document.execCommand (兼容性更好)
              const textArea = document.createElement('textarea')
              textArea.value = textToCopy
              textArea.style.position = 'fixed'
              textArea.style.left = '-999999px'
              textArea.style.top = '-999999px'
              document.body.appendChild(textArea)
              textArea.focus()
              textArea.select()
              
              const successful = document.execCommand('copy')
              document.body.removeChild(textArea)
              
              if (successful) {
                copyBtn.textContent = '已复制'
                setTimeout(() => { copyBtn.textContent = '复制' }, 2000)
              } else {
                throw new Error('复制失败')
              }
            } catch (error) {
              // 方法3: 降级方案 - 提示用户手动复制
              copyBtn.textContent = '点击选择'
              copyBtn.onclick = (e) => {
                e.stopPropagation()
                const range = document.createRange()
                range.selectNodeContents(code)
                const selection = window.getSelection()
                selection.removeAllRanges()
                selection.addRange(range)
                copyBtn.textContent = '已选择'
                setTimeout(() => { copyBtn.textContent = '复制' }, 2000)
              }
            }
          }
          pre.appendChild(copyBtn)
        }
      })
    },
    addGlobalStyles() {
      if (this.globalStyleEl) return
      const styleEl = document.createElement('style')
      styleEl.id = 'article-global-styles-mobile'
      styleEl.textContent = `
        .markdown-display p code {
          padding: 0.2em 0.4em;
          margin: 0 0.2em;
          background-color: #f6f8fa;
          color: #476582;
          border-radius: 3px;
          font-size: 0.9em;
          font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
          white-space: nowrap;
        }

        .markdown-display li code {
          padding: 0.2em 0.4em;
          margin: 0 0.2em;
          background-color: #f6f8fa;
          color: #476582;
          border-radius: 3px;
          font-size: 0.9em;
          font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
          white-space: nowrap;
        }

        .markdown-content {
          padding: 0 15px 0 15px;
          box-sizing: border-box;
          font-size: 15px !important;
          line-height: 1.75 !important;
          color: #2c3e50 !important;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif !important;
        }
        .markdown-display {
          font-size: 15px !important;
          line-height: 1.75 !important;
          color: #2c3e50 !important;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif !important;
        }
        .display-math-colored {
          text-align: center !important;
          display: block !important;
          margin: 1em 0 !important;
          width: 100% !important;
          overflow-x: auto !important;
          overflow-y: hidden !important;
        }
        
        .display-math-colored .MathJax_SVG_Display,
        .display-math-colored .MathJax_Display {
          margin: 0 !important;
          padding: 0 !important;
          background: transparent !important;
          box-shadow: none !important;
        }
        .markdown-display pre {
          background-color: #0d1117;
          padding: 0;
          border-radius: 0 !important;
          overflow: hidden;
          position: relative;
          width: 100% !important;
          margin: 16px 0;
          border: 1px solid #30363d;
          display: flex;
          flex-direction: column;
        }
        .markdown-display pre code {
          color: #c9d1d9;
          font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
          font-size: 14px;
          line-height: 1.5;
          display: block;
          padding: 16px 16px 16px 50px !important;
          overflow-x: auto;
          tab-size: 4;
          white-space: pre;
        }
        .markdown-display pre .line-numbers {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          padding: 16px 8px;
          color: #6e7681;
          text-align: right;
          background-color: #161b22;
          border-right: 1px solid #30363d;
          user-select: none;
          font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
          white-space: pre;
          line-height: 1.5;
          font-size: 14px;
          width: 40px;
          box-sizing: border-box;
          z-index: 1;
        }
        .markdown-display pre .copy-code-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          padding: 4px 8px;
          background: rgba(110, 118, 129, 0.4);
          color: #c9d1d9;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.2s;
          z-index: 10;
        }
        .markdown-display pre:hover .copy-code-btn {
          opacity: 1;
        }
        .markdown-display pre .copy-code-btn:hover {
          background: rgba(110, 118, 129, 0.6);
        }
        
        /* 任务列表样式 - 移动端优化 */
        .markdown-display ul.task-list {
          list-style: none !important;
          padding-left: 10px !important; /* 任务列表左padding改为10px */
        }
        .markdown-display ul.task-list li.task-list-item {
          list-style: none !important;
          display: flex !important;
          align-items: center !important;
        }
        .markdown-display ul.task-list li.task-list-item::before {
          display: none !important; /* 取消小黑点显示 */
        }
        .markdown-display ul.task-list li.task-list-item input[type="checkbox"] {
          margin: 0 8px 0 0 !important;
          flex-shrink: 0 !important;
          transform: scale(1) !important;
          vertical-align: middle !important;
          position: relative !important;
          top: -1px !important;
        }
        .markdown-display ul.task-list li.task-list-item > span,
        .markdown-display ul.task-list li.task-list-item > p {
          margin: 0 !important;
          flex: 1 !important;
          line-height: 1.5 !important;
          display: inline-block !important;
          vertical-align: middle !important;
        }

        /* 当列表中既有任务项又有普通项时，恢复普通项标记，避免小黑点丢失 */
        .markdown-display ul:has(li input[type="checkbox"]) > li:not(.task-list-item) {
          list-style: disc !important;
        }
        .markdown-display ul.task-list > li:not(.task-list-item) {
          list-style: disc !important;
        }

        /* 顶层及通用列表统一上下间距，避免相邻列表出现默认1em */
        .markdown-display > .markdown-content > ul,
        .markdown-display > .markdown-content > ol,
        .markdown-display .markdown-content > ul,
        .markdown-display .markdown-content > ol {
          margin-top: 0 !important;
          margin-bottom: 0 !important;
        }
        .markdown-display ul,
        .markdown-display ol,
        .markdown-display .markdown-content ul,
        .markdown-display .markdown-content ol {
          margin-top: 0 !important;
          margin-bottom: 0 !important;
        }

        /* 移除列表项内段落的默认外边距，避免撑高上下间距 */
        .markdown-display ul li > p,
        .markdown-display ol li > p,
        .markdown-display .markdown-content ul li > p,
        .markdown-display .markdown-content ol li > p {
          margin: 0 !important;
        }
        
        /* 有序列表、无序列表样式调整 */
        .markdown-display ul,
        .markdown-display ol {
          padding-left: 30px !important; /* 有序列表、无序列表左padding改为30px */
        }
        /* 非任务列表强制保持30px左内边距 */
        .markdown-display ul:not(.task-list):not(:has(li input[type="checkbox"])) {
          padding-left: 30px !important;
        }
        /* 混合列表中普通项仍按父ul的30px缩进显示 */
        .markdown-display ul:has(li input[type="checkbox"]) > li:not(.task-list-item) {
          padding-left: 0 !important;
        }
        
        .markdown-display blockquote {
          margin: 20px 0 !important;
          padding: 15px 20px 15px 35px !important;
          color: #476582 !important;
          background-color: rgba(235, 245, 255, 0.7) !important;
          border-left: 4px solid #409EFF !important;
          border-radius: 0 4px 4px 0 !important;
          position: relative !important;
          display: block !important;
          box-sizing: border-box !important;
        }
        .markdown-display blockquote::before {
          content: '"';
          position: absolute !important;
          left: 10px !important;
          top: 8px !important;
          font-size: 20px !important;
          color: #409EFF !important;
          opacity: 0.5 !important;
        }
        .markdown-display table {
          border-collapse: collapse;
          width: 100%;
          margin: 16px 0;
          display: block;
          overflow-x: auto;
        }
        .markdown-display table th,
        .markdown-display table td {
          padding: 8px 12px;
          border: 1px solid #dfe2e5;
        }
        .markdown-display table th {
          background-color: #f6f8fa;
          font-weight: 600;
        }
        .markdown-display table tr:nth-child(2n) {
          background-color: #f8f8f8;
        }
        .copyright-info {
          text-align: center !important;
          padding: 6px 10px 8px !important;
          margin-top: 5px !important;
          margin-bottom: 5px !important;
          font-size: 12px !important;
          line-height: 1.5 !important;
          position: static !important;
          display: block !important;
          height: auto !important;
          overflow: visible !important;
          white-space: normal !important;
          color: #909399 !important;
          background: none !important;
        }
        .mobile-copyright {
          display: flex !important;
          flex-direction: column !important;
          gap: 1px !important;
          width: 100% !important;
          text-align: center !important;
          white-space: normal !important;
          color: #909399 !important;
          font-size: 12px !important;
          line-height: 1.2 !important;
          padding-bottom: 8px !important;
        }
         /* 目录弹窗样式 */
         .mobile-navigation-overlay {
           position: fixed !important;
           top: 0 !important;
           left: 0 !important;
           right: 0 !important;
           bottom: 0 !important;
           background-color: rgba(0, 0, 0, 0.5) !important;
           z-index: 3000 !important;
           display: flex !important;
           align-items: center !important;
           justify-content: center !important;
         }
         .mobile-navigation-card {
           width: 85% !important;
           max-width: 350px !important;
           max-height: 80% !important;
           border-radius: 8px !important;
           overflow: hidden !important;
           display: flex !important;
           flex-direction: column !important;
           box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
         }
         .mobile-navigation-header {
           padding: 12px 15px !important;
           display: flex !important;
           justify-content: space-between !important;
           align-items: center !important;
           border-bottom: 1px solid #ebeef5 !important;
         }
         .mobile-navigation-header .title-wrapper {
           font-size: 16px !important;
           font-weight: 600 !important;
           color: #303133 !important;
         }
         .mobile-navigation-header i {
           font-size: 18px !important;
           color: #909399 !important;
           cursor: pointer !important;
         }
         .mobile-navigation-header i:hover {
           color: #409eff !important;
         }
         .mobile-navigation-content {
           flex: 1 !important;
           overflow-y: auto !important;
         }
         .mobile-navigation-item {
           padding: 10px 15px !important;
           font-size: 14px !important;
           color: #606266 !important;
           cursor: pointer !important;
           transition: all 0.3s !important;
           white-space: nowrap !important;
           overflow: hidden !important;
           text-overflow: ellipsis !important;
         }
         .mobile-navigation-item.active {
           background-color: #ecf5ff !important;
           color: #409eff !important;
           border-right: 3px solid #409eff !important;
         }
         .mobile-navigation-item:hover {
           background-color: #f5f7fa !important;
           color: #409eff !important;
         }
         .mobile-navigation-empty {
           padding: 15px !important;
           text-align: center !important;
           color: #909399 !important;
           font-size: 14px !important;
         }
        
        /* 数学公式样式 - 移动端优化 */
        .markdown-display .MathJax_Display,
        .markdown-display .MathJax_SVG_Display,
        .markdown-display .mjx-chtml.MJXc-display {
          overflow-x: auto !important;
          overflow-y: hidden !important;
          margin: 1em 0 !important;
          padding: 0.5em 0 !important;
          background: transparent !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          text-align: center !important;
          display: block !important;
          width: 100% !important;
          position: relative !important;
          border-bottom: none !important;
        }
        
        /* 实际公式元素 */
        .markdown-display .MathJax_Display .MathJax,
        .markdown-display .MathJax_SVG_Display .MathJax_SVG {
          cursor: pointer !important;
          display: inline-block !important;
          margin: 0 auto !important;
        }
        
        /* 改进行间公式容器样式 */
        .MathJax_SVG_Display, .MathJax_Display {
          display: block !important;
          text-align: center !important;
          margin: 1em 0 !important;
          background: transparent !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          border-bottom: none !important;
        }
        
        /* 彩色行间公式样式 - 特殊处理 */
        .MathJax_SVG[style*="color:red"], .MathJax[style*="color:red"],
        .MathJax_SVG[style*="color:blue"], .MathJax[style*="color:blue"],
        .MathJax_SVG[style*="color:green"], .MathJax[style*="color:green"],
        .MathJax_SVG[style*="color:purple"], .MathJax[style*="color:purple"],
        .MathJax_SVG[style*="color:orange"], .MathJax[style*="color:orange"] {
          display: inline-block !important;
          margin: 0 auto !important;
        }
        
        /* 强制行间公式容器样式 */
        .markdown-display .MathJax_SVG_Display, .markdown-display .MathJax_Display {
          display: block !important;
          text-align: center !important;
          width: 100% !important;
          margin: 1em 0 !important;
          position: relative !important;
          background: transparent !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          border-bottom: none !important;
        }
        
        /* 行内公式样式 */
        .markdown-display .MathJax:not(.MathJax_Display),
        .markdown-display .MathJax_SVG:not(.MathJax_SVG_Display),
        .markdown-display .mjx-chtml:not(.MJXc-display) {
          margin: 0 0.15em !important;
          color: #0550ae !important;
          vertical-align: middle !important;
          display: inline-block !important;
        }
        
        /* 确保数学公式容器正确处理换行 */
        .MathJax_SVG_Display, .MathJax_Display {
          white-space: normal !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
        }
        
        /* 数学公式内容样式 */
        .MathJax_SVG_Display .MathJax_SVG,
        .MathJax_Display .MathJax {
          white-space: normal !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
        }
        
        /* 隐藏数学公式滚动条 */
        .markdown-display .MathJax_SVG_Display::-webkit-scrollbar {
          display: none !important;
        }
        
        .markdown-display .MathJax_Display::-webkit-scrollbar {
          display: none !important;
        }
        
        .display-math-colored::-webkit-scrollbar {
          display: none !important;
        }

        /* 自定义容器样式 */
        .custom-container {
          margin: 16px 0;
          border-radius: 6px;
          overflow: hidden;
          border: 1px solid;
        }
        .container-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          cursor: pointer;
          user-select: none;
        }
        .container-icon { font-size: 16px; }
        .container-title { font-weight: 600; font-size: 14px; color: #303133; flex: 1; }
        .container-toggle { font-size: 12px; color: #909399; transition: transform 0.2s; margin-left: auto; }
        .custom-container.collapsed .container-content { display: none; }
        .custom-container:not(.collapsed) .container-toggle { transform: rotate(90deg); }
        .container-content {
          padding: 0 14px 12px;
          font-size: 14px;
          line-height: 1.7;
          color: #2c3e50;
        }
        @media (prefers-color-scheme: dark) {
          .container-title { color: #e0e0e0; }
          .container-content { color: #c9d1d9; }
        }
      `
      document.head.appendChild(styleEl)
      this.globalStyleEl = styleEl
    },
    removeGlobalStyles() {
      if (this.globalStyleEl) {
        this.globalStyleEl.parentNode.removeChild(this.globalStyleEl)
        this.globalStyleEl = null
      }
    },
    handleContentClick(e) {
      // 目录锚点跳转
      if (e.target.tagName === 'A' && e.target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault()
        const id = e.target.getAttribute('href').substring(1)
        this.scrollToHeading(id)
      }
    },
    getArticleTypeStyle(type) {
      const styleMap = {
        'OI': '#409EFF',
        '题解': '#67C23A',
        '游记': '#E6A23C',
        '生活': '#F56C6C',
        '学习': '#8E44AD',
        '科技': '#16A085',
        '情感': '#D35400',
        '游戏': '#2980B9',
        '随笔': '#27AE60',
        '其他': '#C0392B'
      }
      return styleMap[type] || ''
    },
    goBack() {
      this.$router.push('/blog')
    },
    toggleNavigation() {
      this.showNavigation = !this.showNavigation
      if (this.showNavigation && this.navigationItems.length === 0) {
        this.scanHeadingsAndGenerateNavigation()
      }
    },
    scanHeadingsAndGenerateNavigation() {
      if (!this.$refs.markdownDisplay) return
      this.navigationItems = []
      const markdownContent = this.$refs.markdownDisplay.querySelector('.markdown-content')
      if (!markdownContent) return
      const headings = markdownContent.querySelectorAll('h1, h2, h3, h4, h5, h6')
      headings.forEach((heading, index) => {
        const level = parseInt(heading.tagName.substring(1))
        const text = heading.textContent.trim()
        const id = heading.id || `heading-${index}`
        heading.id = id
        this.navigationItems.push({ id, level, text })
      })
      if (this.navigationItems.length > 0) {
        this.currentHeadingId = this.navigationItems[0].id
      }
    },
    scrollToHeading(id) {
      const heading = document.getElementById(id)
      if (heading) {
        this.currentHeadingId = id
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    scrollToHeadingMobile(id) {
      this.showNavigation = false
      this.$nextTick(() => {
        this.scrollToHeading(id)
      })
    },
    handleBgSettingsUpdated(settings) {
      this.contentCardStyle = settings.contentCardStyle
      // 创建新的 backgroundLayerStyle 对象，避免修改原始对象
      this.backgroundLayerStyle = { ...settings.backgroundLayerStyle }
      if (this.backgroundLayerStyle && typeof this.backgroundLayerStyle.opacity !== 'undefined') {
        this.backgroundLayerStyle.opacity = this.backgroundLayerStyle.opacity * 0.3
      } else if (this.backgroundLayerStyle && this.backgroundLayerStyle.backgroundImage) {
        this.backgroundLayerStyle.opacity = 0.3
      }
      this.backgroundStyle = settings.backgroundStyle
    },
    // 完全复刻 BlogArticle.vue 的 MathJax 加载与渲染
    loadMathJax() {
      // 清除可能存在的旧MathJax实例
      if (window.MathJax) {
        window.MathJax = null;
      }
      // 加载MathJax配置
      if (!document.getElementById('mathjax-config-mobile')) {
        const mathjaxConfig = document.createElement('script');
        mathjaxConfig.id = 'mathjax-config-mobile';
        mathjaxConfig.type = 'text/x-mathjax-config';
        mathjaxConfig.textContent = `
          MathJax.Hub.Config({
            tex2jax: {
              inlineMath: [['$','$']],
              displayMath: [['$$','$$']],
              processEscapes: true,
              skipTags: ['script', 'noscript', 'style', 'textarea', 'pre']
            },
            messageStyle: 'none',
            showProcessingMessages: false,
            jax: ["input/TeX", "output/SVG"],
            extensions: ["tex2jax.js", "MathZoom.js", "MathMenu.js", "toMathML.js", "TeX/noErrors.js", "TeX/noUndefined.js", "TeX/AMSmath.js", "TeX/AMSsymbols.js", "TeX/color.js"],
            TeX: { 
              extensions: ["AMSmath.js", "AMSsymbols.js", "color.js"],
              Macros: {
                red: ["{\\\color{red}#1}", 1],
                blue: ["{\\\color{blue}#1}", 1],
                green: ["{\\\color{green}#1}", 1],
                purple: ["{\\\color{purple}#1}", 1],
                orange: ["{\\\color{orange}#1}", 1]
              }
            },
            SVG: {
              scale: 110,
              linebreaks: { automatic: true }
            },
            "HTML-CSS": {
              availableFonts: ["TeX"],
              scale: 110
            },
            menuSettings: {
              zoom: "None",
              showRenderer: false,
              showLocale: false,
              showFontMenu: false
            },
            MathMenu: {
              showRenderer: false,
              showLocale: false,
              showFontMenu: false
            },
            MathZoom: {
              delay: 600,
              styles: {
                "#MathJax_Zoom": {
                  "background-color": "#FFFFFF",
                  "box-shadow": "0px 0px 15px #888888",
                  "border": "1px solid #CCCCCC",
                  "padding": "10px",
                  "max-width": "90%",
                  "margin": "0 auto",
                  "z-index": "9999",
                  "position": "fixed",
                  "left": "0",
                  "right": "0",
                  "top": "50px",
                  "overflow": "auto",
                  "max-height": "80vh"
                }
              }
            }
          });
          MathJax.Hub.Register.StartupHook("End", function () {
            // 找到所有行间公式容器
            const displayMathContainers = document.querySelectorAll('.MathJax_SVG_Display, .MathJax_Display');
            displayMathContainers.forEach(container => {
              // 应用居中样式
              container.style.display = 'block';
              container.style.textAlign = 'center';
              
              // 确保位于单独行
              const parentEl = container.parentNode;
              if (parentEl && parentEl.tagName.toLowerCase() !== 'div' && parentEl.tagName.toLowerCase() !== 'p') {
                // 如果公式不在段落或块级元素内，则包装它
                const wrapper = document.createElement('div');
                wrapper.style.display = 'block';
                wrapper.style.textAlign = 'center';
                wrapper.style.margin = '1em 0';
                parentEl.insertBefore(wrapper, container);
                wrapper.appendChild(container);
              }
            });
          });
        `;
        document.head.appendChild(mathjaxConfig);
      }
      // 加载MathJax.js
      if (!document.getElementById('mathjax-js-mobile')) {
        const cdnScript = document.createElement('script');
        cdnScript.id = 'mathjax-js-mobile';
        cdnScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.9/MathJax.js?config=TeX-AMS_SVG';
        cdnScript.async = true;
        cdnScript.crossOrigin = "anonymous";
        cdnScript.onload = () => {
          this.initMathJax();
        };
        document.head.appendChild(cdnScript);
      } else {
        this.initMathJax();
      }
    },
    initMathJax() {
      if (window.MathJax) {
        setTimeout(() => {
          try {
            if (!window.MathJax.Hub) {
              setTimeout(this.initMathJax.bind(this), 500);
              return;
            }
            if (!window.MathJax.OutputJax.SVG) {
              setTimeout(this.initMathJax.bind(this), 500);
              return;
            }
            // 清理可能存在的错误状态
            const elements = document.querySelectorAll('.MathJax_Error');
            elements.forEach(el => el.parentNode.removeChild(el));
            // 渲染公式
            window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub, this.$refs.markdownDisplay]);
          } catch (e) {}
        }, 500);
      } else {
        setTimeout(this.initMathJax.bind(this), 500);
      }
    },
    cleanupMathJax() {
      // 移除 MathJax 配置和脚本
      const config = document.getElementById('mathjax-config-mobile');
      if (config) config.parentNode.removeChild(config);
      const js = document.getElementById('mathjax-js-mobile');
      if (js) js.parentNode.removeChild(js);
      if (window.MathJax) window.MathJax = null;
    },
    // 增强任务列表样式
    enhanceTaskLists() {
      if (!this.$refs.markdownDisplay) return;
      
      console.log('开始增强任务列表样式');
      
      // 拆分混合UL：同一个UL同时包含任务项与普通项时，分离为两个UL
      const uls = this.$refs.markdownDisplay.querySelectorAll('ul');
      uls.forEach(ul => {
        const lis = Array.from(ul.children).filter(n => n.tagName && n.tagName.toLowerCase() === 'li');
        if (lis.length === 0) return;
        const taskLis = lis.filter(li => li.querySelector('input[type="checkbox"]'));
        const normalLis = lis.filter(li => !li.querySelector('input[type="checkbox"]'));
        if (taskLis.length > 0 && normalLis.length > 0) {
          const taskUl = document.createElement('ul');
          taskUl.className = 'task-list';
          taskUl.style.setProperty('padding-left', '10px', 'important');
          taskUl.style.setProperty('list-style', 'none', 'important');
          taskLis.forEach(li => taskUl.appendChild(li));
          const firstIsTask = lis[0].querySelector('input[type="checkbox"]') != null;
          if (firstIsTask) {
            ul.parentNode.insertBefore(taskUl, ul);
          } else {
            ul.parentNode.insertBefore(taskUl, ul.nextSibling);
          }
          ul.style.setProperty('padding-left', '30px', 'important');
        }
      });

      // 查找所有包含复选框的li元素
      const taskItems = this.$refs.markdownDisplay.querySelectorAll('li:has(input[type="checkbox"])');
      console.log(`发现${taskItems.length}个任务列表项`);
      
      taskItems.forEach((item, index) => {
        console.log(`处理任务列表项 ${index + 1}:`, item);
        
        // 添加task-list-item类
        item.classList.add('task-list-item');
        
        // 设置样式
        item.style.display = 'flex';
        item.style.flexWrap = 'wrap';
        item.style.alignItems = 'center';
        item.style.listStyle = 'none';
        const childLists = item.querySelectorAll(':scope > ul, :scope > ol');
        childLists.forEach(cl => {
          cl.style.display = 'block';
          cl.style.width = '100%';
          cl.style.margin = '4px 0 0 0';
        });
        
        // 强制应用样式
        item.style.setProperty('display', 'flex', 'important');
        item.style.setProperty('flex-wrap', 'wrap', 'important');
        item.style.setProperty('align-items', 'center', 'important');
        item.style.setProperty('list-style', 'none', 'important');
      });
      
      // 备用方法：如果:has()选择器不支持
      if (taskItems.length === 0) {
        console.log('使用备用方法处理任务列表');
        const allLis = this.$refs.markdownDisplay.querySelectorAll('li');
        allLis.forEach((item, index) => {
          const checkbox = item.querySelector('input[type="checkbox"]');
          if (checkbox) {
            console.log(`通过备用方法处理任务列表项 ${index + 1}:`, item);
            
            // 这是一个任务列表项
            item.classList.add('task-list-item');
            item.style.display = 'flex';
            item.style.flexWrap = 'wrap';
            item.style.alignItems = 'center';
            item.style.listStyle = 'none';
            const childLists2 = item.querySelectorAll(':scope > ul, :scope > ol');
            childLists2.forEach(cl => {
              cl.style.display = 'block';
              cl.style.width = '100%';
              cl.style.margin = '4px 0 0 0';
            });
            
            // 强制应用样式
            item.style.setProperty('display', 'flex', 'important');
            item.style.setProperty('flex-wrap', 'wrap', 'important');
            item.style.setProperty('align-items', 'center', 'important');
            item.style.setProperty('list-style', 'none', 'important');
            
            // 确保父级ul有正确的缩进
            const parentUl = item.closest('ul');
            if (parentUl) {
              console.log(`通过备用方法设置父级ul缩进:`, parentUl);
              parentUl.style.paddingLeft = '10px';
              parentUl.style.listStyle = 'none';
              // 强制应用样式
              parentUl.style.setProperty('padding-left', '10px', 'important');
              parentUl.style.setProperty('list-style', 'none', 'important');
            }
          }
        });
      }
      
      // 额外检查：直接查找所有ul元素，确保包含复选框的ul有正确样式
      const allUls = this.$refs.markdownDisplay.querySelectorAll('ul');
      allUls.forEach((ul, index) => {
        const hasCheckbox = ul.querySelector('input[type="checkbox"]');
        if (hasCheckbox) {
          console.log(`强制设置包含复选框的ul ${index + 1} 的样式:`, ul);
          ul.style.paddingLeft = '10px';
          ul.style.listStyle = 'none';
          ul.style.setProperty('padding-left', '10px', 'important');
          ul.style.setProperty('list-style', 'none', 'important');
        }
      });
      
      console.log('任务列表样式增强完成');
    },
    
    // 增强数学公式中的颜色
    enhanceMathColors() {
      try {
        // 检查页面中是否有MathJax元素
        const mathJaxElements = document.querySelectorAll('.MathJax_SVG, .MathJax');
        // 处理特殊的math-tex类
        const mathTexElements = document.querySelectorAll('.math-tex');
        mathTexElements.forEach(el => {
          if (el.dataset.processed) return;
          const parentStyle = el.parentElement.style.color;
          if (parentStyle) {
            const mathJaxEl = el.closest('.MathJax_SVG, .MathJax');
            if (mathJaxEl) {
              const mathElems = mathJaxEl.querySelectorAll('.mi, .mo, .mn');
              mathElems.forEach(mathElem => {
                mathElem.style.color = parentStyle;
              });
            }
          }
          el.dataset.processed = 'true';
        });
        
        // 检查所有双美元符号公式
        const displayMathElements = document.querySelectorAll('.MathJax_SVG_Display, .MathJax_Display');
        console.log(`发现${displayMathElements.length}个行间公式`);
        
        // 确保行间公式正确渲染
        displayMathElements.forEach(container => {
          // 确保显示为块级元素
          container.style.display = 'block';
          container.style.textAlign = 'center';
          container.style.width = '100%';
          container.style.margin = '1em 0';
          
          // 处理容器内的公式
          const mathElement = container.querySelector('.MathJax, .MathJax_SVG');
          if (mathElement) {
            // 确保公式居中
            mathElement.style.margin = '0 auto';
            
            // 检查是否包含颜色样式
            if (mathElement.getAttribute('style') && 
                mathElement.getAttribute('style').includes('color:')) {
              console.log('发现彩色公式，应用特殊处理');
              container.dataset.colorFormula = 'true';
            }
          }
          
          // 确保容器是块级元素
          const parent = container.parentElement;
          if (parent && parent.style) {
            // 检查父元素是否为行内元素
            const computedStyle = window.getComputedStyle(parent);
            if (computedStyle.display === 'inline' || computedStyle.display === 'inline-block') {
              // 将父元素改为块级元素
              parent.style.display = 'block';
              parent.style.textAlign = 'center';
              parent.style.width = '100%';
              parent.style.margin = '1em 0';
            }
          }
        });
        
        // 直接用DOM操作修复颜色显示问题
        document.querySelectorAll('.mjx-math [style*="color:"]').forEach(el => {
          console.log('发现带颜色的数学元素:', el.outerHTML.substring(0, 50));
        });
        
        // 给所有双美元符号的公式添加额外的样式强制
        setTimeout(() => {
          const displayMathContainers = document.querySelectorAll('.MathJax_SVG_Display, .MathJax_Display');
          displayMathContainers.forEach(container => {
            if (!container.classList.contains('enhanced')) {
              container.classList.add('enhanced');
              container.style.display = 'block';
              container.style.textAlign = 'center';
              container.style.margin = '1em 0';
              container.style.width = '100%';
              
              // 强制父元素为块级
              let parent = container.parentElement;
              if (parent && parent.tagName.toLowerCase() !== 'div' && 
                  parent.tagName.toLowerCase() !== 'p') {
                // 创建一个新的div元素
                const wrapper = document.createElement('div');
                wrapper.style.display = 'block';
                wrapper.style.width = '100%';
                wrapper.style.textAlign = 'center';
                wrapper.style.margin = '1em 0';
                
                // 替换原来的元素
                parent.insertBefore(wrapper, container);
                wrapper.appendChild(container);
              }
            }
          });
        }, 1000);
      } catch (e) {
        console.error('增强数学颜色时出错:', e);
      }
    },
    
    // 背景设置调试信息
    debugBgSettings() {
      console.log('=== ArticleViewMobile 背景设置调试信息 ===')
      console.log('当前背景设置:', {
        contentCardStyle: this.contentCardStyle,
        backgroundLayerStyle: this.backgroundLayerStyle,
        backgroundStyle: this.backgroundStyle
      })
      
      // 检查背景层元素
      const backgroundLayer = this.$el.querySelector('.background-layer')
      if (backgroundLayer) {
        const computedStyle = window.getComputedStyle(backgroundLayer)
        console.log('背景层元素:', {
          element: backgroundLayer,
          backgroundImage: computedStyle.backgroundImage,
          opacity: computedStyle.opacity,
          zIndex: computedStyle.zIndex
        })
      }
      
      // 检查内容背景层元素
      const contentBgLayer = this.$el.querySelector('.content-bg-layer')
      if (contentBgLayer) {
        const computedStyle = window.getComputedStyle(contentBgLayer)
        console.log('内容背景层元素:', {
          element: contentBgLayer,
          backgroundColor: computedStyle.backgroundColor,
          opacity: computedStyle.opacity,
          zIndex: computedStyle.zIndex,
          position: computedStyle.position
        })
      }
      
      // 检查文章内容元素
      const articleContent = this.$el.querySelector('.article-content')
      if (articleContent) {
        const computedStyle = window.getComputedStyle(articleContent)
        console.log('文章内容元素:', {
          element: articleContent,
          backgroundColor: computedStyle.backgroundColor,
          opacity: computedStyle.opacity
        })
      }
      
      console.log('=== 背景设置调试信息结束 ===')
    }
  }
}
</script>

<style lang="scss" scoped>
.article-view-mobile {
  min-height: 100vh;
  width: 100vw;
  overflow-x: hidden;
  background: #f0f2f5;
  position: relative;
  display: flex;
  flex-direction: column;
}

/* 背景图层样式，确保背景设置生效 */
.background-layer {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background-size: cover !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
  pointer-events: none !important;
  z-index: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
}

.article-header {
  margin: 0 !important;
  width: 100% !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  background-color: rgba(255, 255, 255, 0.8) !important;
  position: static !important;
  flex-shrink: 0 !important;
  flex: 0 0 auto !important; /* 固定高度，不伸缩 */
  .header-content.mobile-only {
    padding: 12px 15px 15px !important;
    box-sizing: border-box !important;
    width: 100% !important;
    overflow: visible !important;
    position: relative;
    .header-row {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      position: relative;
      margin-bottom: 10px;
    }
    .article-title {
      font-size: 20px !important;
      line-height: 1.3 !important;
      margin: 0 0 10px 0 !important;
      font-weight: 600 !important;
      color: #1f2937 !important;
      text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3) !important;
      flex: 1;
    }
    .publish-status-stamp {
      position: absolute;
      top: 65px;
      right: 15px;
      transform: rotate(-15deg);
      pointer-events: none;
      z-index: 2;
      .stamp-content {
        width: 60px;
        height: 32px;
        border-radius: 16px;
        color: white;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 14px;
        font-weight: bold;
        border: 2px solid rgba(255, 255, 255, 0.6);
        box-shadow: 0 0 8px rgba(0, 0, 0, 0.15);
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        background-color: rgba(103, 194, 58, 0.6);
      }
      &.hidden .stamp-content {
        background-color: rgba(245, 108, 108, 0.6);
      }
      &.published .stamp-content {
        background-color: rgba(103, 194, 58, 0.6);
      }
    }
    .time-item {
      font-size: 12px !important;
      color: #4b5563 !important;
      margin-bottom: 4px !important;
      display: flex !important;
      align-items: center !important;
      i {
        margin-right: 4px !important;
        font-size: 13px !important;
      }
    }
    .meta-row {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      flex-wrap: wrap !important;
      margin-top: 8px !important;
      width: 100% !important;
      .left-items {
        display: flex !important;
        align-items: center !important;
        flex-wrap: wrap !important;
        gap: 6px !important;
        flex: 1 !important;
        max-width: 75% !important;
      }
      .article-type {
        font-size: 12px !important;
        padding: 1px 8px !important;
        height: 22px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        text-align: center !important;
        box-shadow: none !important;
        border: none !important;
      }
      .article-tag {
        font-size: 11px !important;
        padding: 0 6px !important;
        height: 20px !important;
        line-height: 18px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }
      
      .read-count {
        font-size: 12px !important; /* 保持原字号 */
        color: #ffffff !important; /* 文字改为白色 */
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 4px !important;
        white-space: nowrap !important;
        padding: 3px 10px !important; /* 胶囊内边距 */
        margin-right: 0 !important;
        margin-left: auto !important;
        flex-shrink: 0 !important;
        background: rgba(64, 158, 255, 0.5) !important; /* 浅蓝背景，与首页移动端一致风格 */
        border-radius: 20px !important; /* 胶囊形状 */
        backdrop-filter: blur(2px) !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;

        i {
          color: #ffffff !important; /* 图标改为白色 */
          margin-right: 3px !important;
          font-size: 13px !important; /* 保持原大小 */
        }
      }
    }
  }
  :deep(.el-card__body) {
    padding: 0 !important;
    background-color: transparent !important;
  }
}

/* 正文容器样式 */
.content-container {
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  min-height: calc(100vh - 200px) !important; /* 减去头部高度 */
}

.main-content-wrapper {
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
}

.article-content {
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  min-height: 100% !important;
  
  :deep(.el-card__body) {
    padding: 0 !important;
    background-color: transparent !important;
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
  }
}

.custom-markdown-container {
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  min-height: 100% !important;
}

.markdown-display {
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  min-height: 100% !important;

  :deep(img) {
    max-width: 100% !important;
    height: auto !important;
    display: block !important;
  }
}

.markdown-content {
  flex: 1 !important;
  min-height: 0 !important;
}

.copyright-divider {
  height: 1px !important;
  background: linear-gradient(to right, transparent, #e4e7ed, transparent) !important;
  margin: 20px 15px 0 15px !important;
  border-radius: 1px !important;
}

.copyright-info {
  margin-top: auto !important;
  padding-top: 20px !important;
  padding-bottom: 20px !important;
  text-align: center !important;
}

.mobile-action-buttons {
  position: fixed !important;
  right: 15px !important;
  bottom: 130px !important;
  z-index: 2001 !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 15px !important;
  align-items: center !important;
  .mobile-button-row {
    width: 40px !important;
    height: 40px !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }
  .mobile-icon-btn {
    width: 40px !important;
    height: 40px !important;
    border-radius: 40px !important;
    background-color: rgba(255, 255, 255, 0.5) !important;
    color: #409eff !important;
    border: none !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    padding: 0 !important;
    transition: all 0.3s ease !important;
    margin: 0 !important;
    i {
      font-size: 20px !important;
    }
    &:hover, &:focus {
      background-color: rgba(255, 255, 255, 0.8) !important;
      transform: scale(1.05) !important;
    }
    &:active {
      background-color: rgba(255, 255, 255, 0.9) !important;
      transform: scale(0.95) !important;
    }
  }

}

/* 隐藏所有滚动条 */
::-webkit-scrollbar {
  display: none !important;
}
* {
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}
</style>